import { klientfeilSvarSchema } from '@es/shared';
import { apiHent } from './klient.js';

/** Skjemaets tak. Kuttes her, ellers avvises meldingen og id-en går tapt. */
const MAKS_MELDING = 300;
const MAKS_STABEL = 4000;

/**
 * Gjør en hvilken som helst kastet verdi om til noe skjemaet godtar.
 *
 * `throw 'noe gikk galt'` er lovlig JavaScript, og en `Error` kan ha tom
 * melding. Begge deler ville gitt 400 fra serveren — altså: nøyaktig de
 * rareste feilene, de man helst vil ha en id på, ville vært de eneste uten.
 */
export function tilMelding(feil: unknown): string {
  const rå =
    feil instanceof Error && feil.message !== ''
      ? `${feil.name}: ${feil.message}`
      : typeof feil === 'string' && feil.trim() !== ''
        ? feil
        : 'Ukjent feil uten melding.';
  const ren = rå.replace(/\s+/g, ' ').trim();
  return ren.length <= MAKS_MELDING ? ren : `${ren.slice(0, MAKS_MELDING - 1)}…`;
}

/**
 * Melder feilen til serveren og gir tilbake id-en å vise.
 *
 * KASTER ALDRI. Den kalles fra en feilgrense som allerede tegner en
 * feilmelding; kastet den videre, ville et nettverksbrudd erstattet en lesbar
 * skjerm med den hvite siden grensen skulle fjerne.
 *
 * `null` betyr at ingen id kunne skaffes — typisk fordi brukeren ikke er
 * innlogget, eller fordi API-et er nede. Skjermen skal da ikke vise noen id i
 * det hele tatt. En id ingen kan slå opp er verre enn ingen id: den flytter
 * feilsøkingen til et blindspor og får brukeren til å tro at hun er hjulpet.
 */
/**
 * Meldinger som allerede er sendt, med løftet om svaret.
 *
 * Ligger på modulnivå fordi komponenten ikke kan holde på det: React kaller
 * `componentDidCatch` MER ENN ÉN GANG for det samme uhellet — målt til to i
 * produksjonsbygget — og hver av dem ville ellers vært en egen forespørsel.
 *
 * Det er ikke en optimalisering. To innmeldinger gir to logglinjer og to
 * sporings-ID-er for én ting som gikk galt, og da kan brukeren lese opp den ene
 * mens support leter etter den andre.
 */
const paagaaende = new Map<string, Promise<string | null>>();

/** Melder feilen én gang, uansett hvor mange ganger React fanger den. */
export function meldEnGang(
  feil: unknown,
  komponentstabel: string | undefined,
  sti: string,
): Promise<string | null> {
  const nokkel = tilMelding(feil);
  const alt = paagaaende.get(nokkel);
  if (alt !== undefined) return alt;

  const lovnad = meldKlientfeil(feil, komponentstabel, sti);
  paagaaende.set(nokkel, lovnad);
  return lovnad;
}

/** Bare for tester: tømmer det som er husket mellom to tilfeller. */
export function glemMeldteFeil(): void {
  paagaaende.clear();
}

export async function meldKlientfeil(
  feil: unknown,
  komponentstabel: string | undefined,
  sti: string,
): Promise<string | null> {
  try {
    const svar = await apiHent('/klientfeil', klientfeilSvarSchema, {
      method: 'POST',
      body: JSON.stringify({
        melding: tilMelding(feil),
        ...(komponentstabel === undefined
          ? {}
          : { komponentstabel: komponentstabel.slice(0, MAKS_STABEL) }),
        sti: sti.slice(0, 500),
      }),
    });
    return svar.sporingsId;
  } catch {
    return null;
  }
}

/**
 * Om feilen er en modul som ikke lot seg hente.
 *
 * Skilles ut fordi den ene feilen krever en annen handling enn alle andre.
 * Resten kan som regel repareres ved å tegne skjermen på nytt. Denne kan ikke:
 * `React.lazy` husker det AVVISTE løftet, så å nullstille grensen rendrer den
 * samme modulen, som kaster igjen umiddelbart uten å spørre nettverket. En
 * «Prøv igjen»-knapp ville sett ut som et tilbud og vært en blindvei.
 *
 * Den er også den desidert vanligste i produksjon, og har en konkret årsak:
 * et nytt bygg er rullet ut mens fanen sto åpen, og index-en i nettleseren
 * peker på en bunt som ikke finnes lenger. Full innlasting er da ikke en
 * nødløsning — det er nøyaktig riktig svar, og teksten kan si hvorfor.
 *
 * Meldingene er nettleserens egne og varierer: Chrome og Firefox sier «failed
 * to fetch dynamically imported module», Safari «Importing a module script
 * failed». Alle tre må med, ellers gjelder dette bare noen av brukerne.
 */
export function erModullastfeil(feil: unknown): boolean {
  const tekst = (feil instanceof Error ? feil.message : String(feil)).toLowerCase();
  return tekst.includes('dynamically imported module') || tekst.includes('module script failed');
}
